/**
 * Contacts surface (live) - pure filter/sort/format helpers over the live
 * `Contact` domain model. No React, no writes. Seniority and Function are
 * inferred from the title (there is no server column), which is what the filter
 * dropdowns' "inferred from title" caption honestly reflects.
 */

import type { Contact, ContactPatch, ContactTemperature } from "./types";

export type Seniority = "C-Suite" | "VP" | "Director" | "Manager" | "Other";

export type FunctionArea =
  | "Technology"
  | "Finance"
  | "Operations"
  | "Strategy"
  | "HR"
  | "Legal"
  | "Supply Chain"
  | "Marketing"
  | "Sales"
  | "Other";

export interface ContactFilters {
  search: string;
  temperature: ContactTemperature | "all";
  sourceGroup: string; // "all" or a source (sourceDetail) label
  followUpDue: boolean; // 8b pill
  activeEngagement: boolean; // Active Delivery Graph engagement (firm-wide derived)
  seniority: Seniority[];
  functions: FunctionArea[];
  company: string; // "" = any
  titleContains: string;
  ownerId: string; // "all" or an owner id (admin All scope only)
}

export const SENIORITY_OPTIONS: Seniority[] = [
  "C-Suite",
  "VP",
  "Director",
  "Manager",
  "Other",
];

export const FUNCTION_OPTIONS: FunctionArea[] = [
  "Technology",
  "Finance",
  "Operations",
  "Strategy",
  "HR",
  "Legal",
  "Supply Chain",
  "Marketing",
  "Sales",
  "Other",
];

export const TEMPERATURE_LABEL: Record<ContactTemperature, string> = {
  hot: "Hot",
  warm: "Warm",
  cold: "Cold",
};

/** Follow-up is "due" when the reminder date is within this many days or past. */
export const FOLLOW_UP_DAYS = 7;

export function inferSeniority(title: string): Seniority {
  const t = title.toLowerCase();
  if (
    /\b(chief|ceo|cfo|coo|cto|cio|ciso|chro|cmo|clo|founder|co-founder|president|owner|managing partner|managing director|general counsel)\b/.test(
      t,
    )
  ) {
    return "C-Suite";
  }
  if (/\b(vp|svp|evp|vice president|head of)\b/.test(t)) return "VP";
  if (/\bdirector\b/.test(t)) return "Director";
  if (/\b(manager|lead|principal|partner)\b/.test(t)) return "Manager";
  return "Other";
}

export function inferFunction(title: string): FunctionArea {
  const t = title.toLowerCase();
  if (/\b(legal|counsel|compliance|general counsel|regulatory)\b/.test(t)) {
    return "Legal";
  }
  if (/\b(supply chain|procurement|sourcing|logistics)\b/.test(t)) {
    return "Supply Chain";
  }
  if (
    /\b(finance|financial|accounting|controller|treasury|treasurer|audit|fp&a)\b/.test(
      t,
    )
  ) {
    return "Finance";
  }
  if (
    /\b(technology|technical|engineering|engineer|software|data|digital|it|information|platform|infrastructure|security)\b/.test(
      t,
    )
  ) {
    return "Technology";
  }
  if (/\b(hr|human resources|people|talent|recruit)\b/.test(t)) return "HR";
  if (
    /\b(strategy|strategic|corporate development|transformation|m&a|mergers|business development)\b/.test(
      t,
    )
  ) {
    return "Strategy";
  }
  if (/\b(marketing|brand|communications|demand|growth)\b/.test(t)) {
    return "Marketing";
  }
  if (/\b(sales|revenue|commercial|account executive|business dev)\b/.test(t)) {
    return "Sales";
  }
  if (/\b(operations|operating|production|manufacturing|delivery)\b/.test(t)) {
    return "Operations";
  }
  return "Other";
}

/**
 * Inference chips shown in the contact header, seniority then function. Honest
 * absence: an inference that resolves to the "Other" catch-all carries no signal,
 * so it is omitted rather than rendered as a chip; each facet is independent, so a
 * known seniority with an unknown function yields a single chip. Filtering is
 * unaffected - inferSeniority/inferFunction still return "Other" for an unknown
 * title, so the seniority/function "Other" facet keeps matching; only this header
 * display suppresses the empty catch-all.
 */
export function inferenceChips(title: string): (Seniority | FunctionArea)[] {
  const chips: (Seniority | FunctionArea)[] = [];
  const seniority = inferSeniority(title);
  if (seniority !== "Other") chips.push(seniority);
  const functionArea = inferFunction(title);
  if (functionArea !== "Other") chips.push(functionArea);
  return chips;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

/** Whole days from `now` to an ISO date (positive future, negative past), UTC. */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const then = new Date(iso);
  const dayMs = 86_400_000;
  const a = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const b = Date.UTC(then.getUTCFullYear(), then.getUTCMonth(), then.getUTCDate());
  return Math.round((b - a) / dayMs);
}

/** A stored reminder is "due" when it lands within FOLLOW_UP_DAYS or is past. */
export function isFollowUpDue(contact: Contact, now: Date = new Date()): boolean {
  if (!contact.followUpDueAt) return false;
  return daysUntil(contact.followUpDueAt, now) <= FOLLOW_UP_DAYS;
}

/** A reminder is "overdue" once its date is strictly in the past. Overdue is a
 *  subset of due, so an overdue contact still matches the follow-up-due pill; it
 *  only earns the stronger "overdue" treatment on the card and modal. */
export function isFollowUpOverdue(contact: Contact, now: Date = new Date()): boolean {
  if (!contact.followUpDueAt) return false;
  return daysUntil(contact.followUpDueAt, now) < 0;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/** "Apr 20, 2026" from an ISO date, UTC so the day never drifts. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const m = MONTHS[d.getUTCMonth()] ?? "";
  return `${m} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/**
 * Display label for an event/source group. Legacy `event_source` values are
 * contaminated with baked-in filter metadata, e.g.
 * "LinkedIn Contacts [Seniority: C-Suite, VP | Function: ...]", stored verbatim.
 * Until the backend scrubs the column, strip the bracketed segment (from the
 * first " [" to its matching "]") for display, pill grouping, and filtering, so
 * a contaminated variant and a clean "LinkedIn Contacts" collapse to one group.
 * The raw value in the data is never mutated. Any suffix after the "]" is kept.
 */
export function cleanSourceGroup(raw: string | null | undefined): string {
  if (!raw) return "";
  const open = raw.indexOf(" [");
  if (open < 0) return raw.trim();
  const close = raw.indexOf("]", open);
  const cleaned = close < 0 ? raw.slice(0, open) : raw.slice(0, open) + raw.slice(close + 1);
  return cleaned.replace(/\s+/g, " ").trim();
}

/**
 * Suggestions for the Event/Source combobox. Lists ALL groups when the field is
 * empty or holds an existing group unchanged (so the user can switch to any), and
 * filters by substring once they type something new (which they can save as a new
 * group). This is the fix for the field collapsing to only the current value.
 */
export function matchSourceGroups(value: string, options: string[]): string[] {
  const q = value.trim().toLowerCase();
  const exact = options.some((o) => o === value);
  return q === "" || exact ? options : options.filter((o) => o.toLowerCase().includes(q));
}

/** The card/modal "met at" microline from the event/source group and date met.
 *  Renders only what is present (honest absence): "Web Summit 2025", or
 *  "Met Apr 20, 2026", or "Web Summit 2025 · Met Apr 20, 2026". Empty when
 *  neither is known (the caller omits the line entirely). The source group is
 *  shown by its cleaned display label. */
export function metAtLine(contact: Contact): string {
  const parts: string[] = [];
  const group = cleanSourceGroup(contact.sourceDetail);
  if (group) parts.push(group);
  if (contact.dateMet) parts.push(`Met ${formatDate(contact.dateMet)}`);
  return parts.join(" · ");
}

/** Filter patch for a temperature pill. The "All" pill is a single reset for the
 *  whole pill row: it also clears the follow-up-due and active-engagement
 *  toggles (its row-mates). The Hot/Warm/Cold pills change only the temperature
 *  facet, leaving the toggles independent. */
export function temperaturePillPatch(
  key: ContactTemperature | "all",
): Partial<ContactFilters> {
  return key === "all"
    ? { temperature: "all", followUpDue: false, activeEngagement: false }
    : { temperature: key };
}

type Facet = keyof ContactFilters;

export function matchesFilters(
  contact: Contact,
  filters: ContactFilters,
  except?: Facet,
): boolean {
  if (except !== "search" && filters.search.trim()) {
    const q = filters.search.trim().toLowerCase();
    const hay = [
      contact.name,
      contact.company,
      contact.title,
      contact.email,
      contact.sourceDetail ?? "",
    ]
      .join(" ")
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (except !== "temperature" && filters.temperature !== "all") {
    if (contact.temperature !== filters.temperature) return false;
  }
  if (except !== "sourceGroup" && filters.sourceGroup !== "all") {
    // Match on the cleaned display label so a pill selects every raw variant
    // (e.g. "LinkedIn Contacts" and "LinkedIn Contacts [Seniority: ...]").
    if (cleanSourceGroup(contact.sourceDetail) !== filters.sourceGroup) return false;
  }
  if (except !== "followUpDue" && filters.followUpDue) {
    if (!isFollowUpDue(contact)) return false;
  }
  if (except !== "activeEngagement" && filters.activeEngagement) {
    if (!contact.activeEngagement) return false;
  }
  if (except !== "seniority" && filters.seniority.length > 0) {
    if (!filters.seniority.includes(inferSeniority(contact.title))) return false;
  }
  if (except !== "functions" && filters.functions.length > 0) {
    if (!filters.functions.includes(inferFunction(contact.title))) return false;
  }
  if (except !== "company" && filters.company) {
    if (contact.company !== filters.company) return false;
  }
  if (except !== "titleContains" && filters.titleContains.trim()) {
    if (
      !contact.title
        .toLowerCase()
        .includes(filters.titleContains.trim().toLowerCase())
    ) {
      return false;
    }
  }
  if (except !== "ownerId" && filters.ownerId !== "all") {
    if (contact.ownerId !== filters.ownerId) return false;
  }
  return true;
}

export function applyFilters(contacts: Contact[], filters: ContactFilters): Contact[] {
  return contacts.filter((c) => matchesFilters(c, filters));
}

const TEMP_RANK: Record<ContactTemperature, number> = { hot: 0, warm: 1, cold: 2 };
/** Unset ranks after Cold, so an unset contact is never ordered as Hot. */
const UNSET_RANK = 3;
function tempRank(t: ContactTemperature | null): number {
  return t === null ? UNSET_RANK : TEMP_RANK[t];
}

/** Display order: temperature descending (Hot, Warm, Cold, then unset), then
 *  signal count descending, then alphabetical by name. */
export function sortContacts(contacts: Contact[]): Contact[] {
  return [...contacts].sort((a, b) => {
    const byTemp = tempRank(a.temperature) - tempRank(b.temperature);
    if (byTemp !== 0) return byTemp;
    const bySignals = b.matchedSignals.length - a.matchedSignals.length;
    if (bySignals !== 0) return bySignals;
    return a.name.localeCompare(b.name);
  });
}

export interface ScopeStats {
  hot: number;
  warm: number;
  cold: number;
  /** Contacts with no temperature set (server null). Never counted as Hot/Warm/Cold. */
  unset: number;
  withSignals: number;
}

export function scopeStats(contacts: Contact[]): ScopeStats {
  const stats: ScopeStats = { hot: 0, warm: 0, cold: 0, unset: 0, withSignals: 0 };
  for (const c of contacts) {
    if (c.temperature === null) stats.unset += 1;
    else stats[c.temperature] += 1;
    if (c.matchedSignals.length > 0) stats.withSignals += 1;
  }
  return stats;
}

export function temperatureCounts(
  contacts: Contact[],
  filters: ContactFilters,
): Record<ContactTemperature | "all", number> {
  const base = contacts.filter((c) => matchesFilters(c, filters, "temperature"));
  return {
    all: base.length,
    hot: base.filter((c) => c.temperature === "hot").length,
    warm: base.filter((c) => c.temperature === "warm").length,
    cold: base.filter((c) => c.temperature === "cold").length,
  };
}

/** Count of follow-up-due contacts under every OTHER active filter. */
export function followUpDueCount(
  contacts: Contact[],
  filters: ContactFilters,
): number {
  return contacts.filter(
    (c) => matchesFilters(c, filters, "followUpDue") && isFollowUpDue(c),
  ).length;
}

export function activeEngagementCount(
  contacts: Contact[],
  filters: ContactFilters,
): number {
  return contacts.filter(
    (c) => matchesFilters(c, filters, "activeEngagement") && c.activeEngagement,
  ).length;
}

export function sourceGroupCounts(
  contacts: Contact[],
  filters: ContactFilters,
): { all: number; byGroup: Map<string, number> } {
  const base = contacts.filter((c) => matchesFilters(c, filters, "sourceGroup"));
  const byGroup = new Map<string, number>();
  for (const c of base) {
    const g = cleanSourceGroup(c.sourceDetail);
    if (g) byGroup.set(g, (byGroup.get(g) ?? 0) + 1);
  }
  return { all: base.length, byGroup };
}

/** Distinct non-empty source labels (cleaned display labels) present in the
 *  roster. Empty when the live data carries no provenance (honest absence - the
 *  pill row is then omitted). Contaminated variants collapse to one label. */
export function uniqueSourceGroups(contacts: Contact[]): string[] {
  const set = new Set<string>();
  for (const c of contacts) {
    const g = cleanSourceGroup(c.sourceDetail);
    if (g) set.add(g);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function uniqueCompanies(contacts: Contact[]): string[] {
  return Array.from(new Set(contacts.map((c) => c.company).filter(Boolean))).sort(
    (a, b) => a.localeCompare(b),
  );
}

export function uniqueOwners(
  contacts: Contact[],
  viewerId: string,
): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const c of contacts) {
    if (c.ownerId && !seen.has(c.ownerId)) seen.set(c.ownerId, c.ownerName);
  }
  return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => {
    if (a.id === viewerId) return -1;
    if (b.id === viewerId) return 1;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Merge a partial update into a contact, mapping the patch's field names onto
 * the display model (eventSource → sourceDetail, reminderDate → followUpDueAt).
 * Only present keys are applied. Setting a temperature marks its source
 * "stored" (it is now user-set, not signal-derived). Shared by the live hook's
 * single-row cache merge (the responsive path that avoids waiting on the roster
 * refetch) and the mock adapter, so both stay consistent. Signal matches, owner,
 * and contact-log fields are preserved.
 */
export function applyPatchToContact(contact: Contact, patch: ContactPatch): Contact {
  const next: Contact = { ...contact };
  if (patch.name !== undefined) next.name = patch.name;
  if (patch.title !== undefined) next.title = patch.title;
  if (patch.company !== undefined) next.company = patch.company;
  if (patch.email !== undefined) next.email = patch.email;
  if (patch.phone !== undefined) next.phone = patch.phone;
  if (patch.notes !== undefined) next.notes = patch.notes;
  if (patch.linkedin !== undefined) next.linkedin = patch.linkedin;
  if (patch.eventSource !== undefined) next.sourceDetail = patch.eventSource;
  if (patch.dateMet !== undefined) next.dateMet = patch.dateMet;
  if (patch.temperature !== undefined) {
    next.temperature = patch.temperature;
    next.temperatureSource = "stored";
  }
  if (patch.reminderDate !== undefined) next.followUpDueAt = patch.reminderDate;
  if (patch.reminderTime !== undefined) next.reminderTime = patch.reminderTime;
  if (patch.reminderReason !== undefined) next.reminderReason = patch.reminderReason;
  return next;
}

/** Merge the effect of a logged contact: one more logged touch, dated today. */
export function loggedContact(contact: Contact, today: string): Contact {
  return { ...contact, lastTouchAt: today, contactCount: contact.contactCount + 1 };
}
